/**
 * SyncSettings — admin page for configuring automatic sync behaviour.
 *
 * Stores settings in config/syncSettings:
 *   autoSyncEnabled  boolean  — master on/off switch
 *   startHour        number   — Berlin local hour (0–23) when auto-sync begins
 *   endHour          number   — Berlin local hour (0–23) when auto-sync stops
 *
 * During the configured window, Cloud Functions that write to
 * stage_changes_queue or note_events_queue will also set
 * config/orderSync.status = "requested", causing the scraper (if running
 * in watch mode) to process the queue within ~10 seconds.
 *
 * Events that trigger auto-sync:
 *  - Customer confirms or modifies their order
 *  - Customer approves or requests changes to a draft
 *  - New draft uploaded (email sent to customer)
 *  - Welcome email sent (manual or automatic)
 */
import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

const DEFAULTS = {
  autoSyncEnabled: true,
  startHour: 8,
  endHour: 23,
  blockManualOutsideHours: false,
  // Weekend (Sa+So) hours — used only if useWeekendHours=true. Otherwise
  // weekday hours apply on all 7 days.
  useWeekendHours: false,
  weekendStartHour: 9,
  weekendEndHour: 23,
};

const card = {
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  padding: "1.5em 2em",
  marginBottom: "1.5em",
  maxWidth: 520,
};

const labelStyle = {
  display: "block",
  fontWeight: "600",
  fontSize: "0.78rem",
  color: "#4a5568",
  marginBottom: "0.3em",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const inputStyle = {
  width: "80px",
  padding: "0.45em 0.65em",
  border: "1px solid #cbd5e0",
  borderRadius: "6px",
  fontSize: "0.9rem",
  color: "#2d3748",
};

export default function SyncSettings() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [domains, setDomains] = useState("");
  const [savingDomains, setSavingDomains] = useState(false);
  const [savedDomains, setSavedDomains] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "config", "syncSettings"));
        if (snap.exists()) {
          const data = snap.data();
          setForm({ ...DEFAULTS, ...data });
          setDomains((data.problemDomains || []).join("\n"));
        }
      } catch { /* use defaults */ }
      setLoading(false);
    })();
  }, []);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "config", "syncSettings"), form, { merge: true });
      setSaved(true);
    } catch (e) {
      alert("Fehler beim Speichern: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDomains = async () => {
    setSavingDomains(true);
    try {
      const list = domains.split("\n").map(d => d.trim().toLowerCase()).filter(Boolean);
      await setDoc(doc(db, "config", "syncSettings"), { problemDomains: list }, { merge: true });
      setSavedDomains(true);
      setTimeout(() => setSavedDomains(false), 2000);
    } catch (e) {
      alert("Fehler beim Speichern: " + e.message);
    } finally {
      setSavingDomains(false);
    }
  };

  if (loading) {
    return <p style={{ color: "#718096" }}>Lade Einstellungen…</p>;
  }

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 1.25em", fontSize: "1.3rem", color: "#1a202c" }}>
        Auto-Sync-Einstellungen
      </h2>

      <div style={card}>
        {/* Master toggle */}
        <label style={{
          display: "flex", alignItems: "center", gap: "0.75em",
          cursor: "pointer", marginBottom: "1.5em",
        }}>
          <input
            type="checkbox"
            checked={!!form.autoSyncEnabled}
            onChange={(e) => setField("autoSyncEnabled", e.target.checked)}
            style={{ accentColor: "#553c9a", width: 17, height: 17 }}
          />
          <span style={{ fontSize: "0.95rem", fontWeight: 600, color: "#2d3748" }}>
            Auto-Sync aktiviert
          </span>
        </label>

        {/* Hour range */}
        <div style={{
          display: "flex", gap: "2em", alignItems: "flex-end",
          opacity: form.autoSyncEnabled ? 1 : 0.45,
          pointerEvents: form.autoSyncEnabled ? "auto" : "none",
          marginBottom: "1.5em",
        }}>
          <div>
            <label style={labelStyle}>Von (Uhr, Berlin)</label>
            <input
              type="number"
              min={0}
              max={23}
              value={form.startHour}
              onChange={(e) =>
                setField("startHour", Math.min(23, Math.max(0, Number(e.target.value))))}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Bis (Uhr, Berlin)</label>
            <input
              type="number"
              min={0}
              max={23}
              value={form.endHour}
              onChange={(e) =>
                setField("endHour", Math.min(23, Math.max(0, Number(e.target.value))))}
              style={inputStyle}
            />
          </div>
          <div style={{
            fontSize: "0.82rem", color: "#718096",
            paddingBottom: "0.5em", lineHeight: 1.4,
          }}>
            <strong>Mo–Fr Arbeitszeit</strong> (wann Auto-Sync aktiv ist).<br />
            Außerhalb = Ruhezeit (kein Sync).<br />
            z. B. „7 bis 23" = aktiv 07:00–23:00, Ruhe 23:00–07:00.<br />
            <strong>„Bis 0"</strong> = bis Mitternacht (24:00).
          </div>
        </div>

        {/* Weekend hours toggle + fields */}
        <label style={{
          display: "flex", alignItems: "flex-start", gap: "0.75em",
          cursor: "pointer", marginBottom: form.useWeekendHours ? "1em" : "1.5em",
          opacity: form.autoSyncEnabled ? 1 : 0.45,
          pointerEvents: form.autoSyncEnabled ? "auto" : "none",
        }}>
          <input
            type="checkbox"
            checked={!!form.useWeekendHours}
            onChange={(e) => setField("useWeekendHours", e.target.checked)}
            style={{ accentColor: "#553c9a", width: 17, height: 17, marginTop: 3 }}
          />
          <div>
            <span style={{ fontSize: "0.92rem", fontWeight: 600, color: "#2d3748" }}>
              Am Wochenende (Sa+So) abweichende Zeiten
            </span>
            <div style={{ fontSize: "0.82rem", color: "#718096", marginTop: 3, lineHeight: 1.4 }}>
              Menschliches Muster: an Werktagen früh aktiv, am Wochenende
              später. Reduziert Bot-Verdacht bei Etsy — CAPTCHAs feuern
              gerne wenn früh am Sonntag ein Sync läuft (kein realer
              Verkäufer arbeitet Sa/So um 06:00).
            </div>
          </div>
        </label>

        {form.useWeekendHours && (
          <div style={{
            display: "flex", gap: "2em", alignItems: "flex-end",
            opacity: form.autoSyncEnabled ? 1 : 0.45,
            pointerEvents: form.autoSyncEnabled ? "auto" : "none",
            marginBottom: "1.5em",
            paddingLeft: "1.8em",
            borderLeft: "3px solid #e2e8f0",
          }}>
            <div>
              <label style={labelStyle}>Sa/So Von</label>
              <input
                type="number"
                min={0}
                max={23}
                value={form.weekendStartHour}
                onChange={(e) =>
                  setField("weekendStartHour", Math.min(23, Math.max(0, Number(e.target.value))))}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Sa/So Bis</label>
              <input
                type="number"
                min={0}
                max={23}
                value={form.weekendEndHour}
                onChange={(e) =>
                  setField("weekendEndHour", Math.min(23, Math.max(0, Number(e.target.value))))}
                style={inputStyle}
              />
            </div>
            <div style={{
              fontSize: "0.82rem", color: "#718096",
              paddingBottom: "0.5em", lineHeight: 1.4,
            }}>
              <strong>Empfehlung:</strong> z.&nbsp;B.&nbsp;„9 bis 23".<br />
              Deckt sich mit realem Verkäufer-Rhythmus am Wochenende.
            </div>
          </div>
        )}

        {/* Block manual syncs outside operating hours */}
        <label style={{
          display: "flex", alignItems: "flex-start", gap: "0.75em",
          cursor: "pointer", marginBottom: "1.5em",
          opacity: form.autoSyncEnabled ? 1 : 0.45,
          pointerEvents: form.autoSyncEnabled ? "auto" : "none",
        }}>
          <input
            type="checkbox"
            checked={!!form.blockManualOutsideHours}
            onChange={(e) => setField("blockManualOutsideHours", e.target.checked)}
            style={{ accentColor: "#553c9a", width: 17, height: 17, marginTop: 3 }}
          />
          <div>
            <span style={{ fontSize: "0.92rem", fontWeight: 600, color: "#2d3748" }}>
              Auch manuelle Sync-Klicks außerhalb dieser Zeit sperren
            </span>
            <div style={{ fontSize: "0.82rem", color: "#718096", marginTop: 3, lineHeight: 1.4 }}>
              Empfohlen. Verhindert dass in der Nacht spontan große Sync-Läufe
              (z. B. viele Drafts hochgeladen + sofort Sync) den Etsy-Account
              flagged. Muss aktiv deaktiviert werden, wenn nachts trotzdem
              gesynct werden soll.
            </div>
          </div>
        </label>

        {/* Info box */}
        <div style={{
          background: "#f7fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "0.85em 1.1em",
          fontSize: "0.83rem",
          color: "#4a5568",
          lineHeight: 1.6,
          marginBottom: "1.5em",
        }}>
          <strong>Wie funktioniert Auto-Sync?</strong><br />
          Während der eingestellten Zeit löst das System automatisch einen Sync
          aus, sobald relevante Ereignisse auftreten — ohne dass du auf „Sync"
          klicken musst. Der Scraper muss dazu im Watch-Modus laufen.<br /><br />
          <strong>Ausgelöst wird Auto-Sync bei:</strong>
          <ul style={{ margin: "0.4em 0 0 1.2em", padding: 0 }}>
            <li>Kunde bestätigt oder ändert Bestelldetails</li>
            <li>Kunde gibt Entwurf frei oder wünscht Änderungen</li>
            <li>Neuer Entwurf hochgeladen (E-Mail gesendet)</li>
            <li>Willkommens-E-Mail gesendet (manuell oder automatisch)</li>
          </ul>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: "0.65em 2em",
            borderRadius: "7px",
            border: "none",
            background: saving ? "#a0aec0" : "#553c9a",
            color: "#fff",
            fontWeight: "700",
            fontSize: "0.95rem",
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Wird gespeichert…" : saved ? "✓ Gespeichert" : "Speichern"}
        </button>
      </div>

      {/* E-Mail-Ausnahmen */}
      <div style={card}>
        <h3 style={{ margin: "0 0 0.5em", fontSize: "1rem", color: "#2d3748" }}>
          E-Mail-Ausnahmen
        </h3>
        <p style={{ fontSize: "0.85rem", color: "#718096", margin: "0 0 0.85em", lineHeight: 1.5 }}>
          Domains, bei denen E-Mails erfahrungsgemäß nicht ankommen (z.&nbsp;B.{" "}
          <code style={{ fontSize: "0.82rem" }}>privaterelay.appleid.com</code>,{" "}
          <code style={{ fontSize: "0.82rem" }}>gmx.de</code>). Für Bestellungen mit
          diesen Domains erscheint in „Bestätigungen" ein Button zum direkten Posten
          der Bestätigungsanfrage über Etsy-Nachrichten.
        </p>
        <textarea
          rows={6}
          value={domains}
          onChange={(e) => { setDomains(e.target.value); setSavedDomains(false); }}
          placeholder={"privaterelay.appleid.com\ngmx.de"}
          style={{
            width: "100%", fontFamily: "monospace", fontSize: "0.85rem",
            padding: "0.5em 0.65em", border: "1px solid #cbd5e0", borderRadius: "6px",
            boxSizing: "border-box", resize: "vertical", color: "#2d3748",
          }}
        />
        <button
          onClick={handleSaveDomains}
          disabled={savingDomains}
          style={{
            marginTop: "0.75em",
            padding: "0.65em 2em",
            borderRadius: "7px",
            border: "none",
            background: savedDomains ? "#c6f6d5" : savingDomains ? "#a0aec0" : "#553c9a",
            color: savedDomains ? "#276749" : "#fff",
            fontWeight: "700",
            fontSize: "0.95rem",
            cursor: savingDomains ? "not-allowed" : "pointer",
          }}
        >
          {savingDomains ? "Wird gespeichert…" : savedDomains ? "✓ Gespeichert" : "Speichern"}
        </button>
      </div>
    </div>
  );
}
